import React, { useState, useEffect, useCallback, useRef } from 'react';
import { contentAPI } from '../services/api';
import NewsCard from './NewsCard';
import LoadingScreen from './LoadingScreen';
import { WarningIcon } from './Icons';
import styles from '../styles/ContentListView.module.css';

/**
 * All News View Component
 * Displays all news items with search and infinite scroll
 *
 * Props:
 * - onBack: Callback when back button is clicked
 * - onNewsClick: Callback when news item is clicked (id)
 */
function AllNewsView({ onBack, onNewsClick }) {
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const searchTimeoutRef = useRef(null);
  const listContainerRef = useRef(null);

  // Load news
  const loadNews = useCallback(async (search = '') => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await contentAPI.getNews(search);
      setNews(data);
    } catch (err) {
      console.error('[AllNewsView] Error loading news:', err);
      setError(err.message || 'Failed to load news');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadNews();
  }, [loadNews]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadNews(searchQuery);
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, loadNews]);

  // Pull to refresh handler
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    loadNews(searchQuery);
  }, [searchQuery, loadNews]);

  // Pull to refresh detection
  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    let startY = 0;
    let currentY = 0;

    const handleTouchStart = (e) => {
      if (container.scrollTop === 0) {
        startY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (container.scrollTop === 0 && !isRefreshing) {
        currentY = e.touches[0].clientY;
        const pullDistance = currentY - startY;

        if (pullDistance > 80) {
          handleRefresh();
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isRefreshing, handleRefresh]);

  return (
    <div className={styles.contentListView}>
      <div className={styles.listHeader}>
        <button onClick={onBack} className={styles.backButton}>
          ← Back
        </button>
        <h1 className={styles.listTitle}>All News</h1>
      </div>

      <div className={styles.searchContainer}>
        <div className={styles.searchInputWrapper}>
          <input
            type="text"
            placeholder="Search news..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className={styles.clearSearchButton}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div ref={listContainerRef} className={styles.listContainer}>
        {isRefreshing && (
          <div className={styles.refreshIndicator}>
            <div className={styles.refreshSpinner}></div>
          </div>
        )}

        {isLoading && !isRefreshing ? (
          <LoadingScreen text="Loading news..." variant="dots" showImage={true} />
        ) : error ? (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>
              <WarningIcon size={64} />
            </div>
            <div className={styles.errorText}>{error}</div>
            <button onClick={() => loadNews(searchQuery)} className={styles.retryButton}>
              Retry
            </button>
          </div>
        ) : news.length === 0 ? (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyText}>
              {searchQuery ? 'No news found' : 'No news available'}
            </div>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className={styles.clearSearchButtonLarge}>
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {news.map((newsItem) => (
              <div
                key={newsItem.id}
                className={styles.cardWrapper}
              >
                <NewsCard
                  headline={newsItem.headline}
                  sourceUrl={newsItem.sourceUrl}
                  publishedDate={newsItem.publishedDate}
                  heroImage={newsItem.heroImage}
                  summary={newsItem.summary}
                  id={newsItem.id}
                  onClick={onNewsClick}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AllNewsView;
